import React, { useState, useMemo } from 'react';
import { useGoldPrice } from '../hooks/useGoldPrice';
import {
  CALCULATION_CONSTANTS,
  EXPANSION_PLAN,
  DIVIDEND_PROJECTIONS
} from '../lib/services/calculationService';
import { AnimatedNumber, AnimatedCurrency } from '../components/common/AnimatedNumber';
import { InfoTooltip, TERM_EXPLANATIONS } from '../components/common/InfoTooltip';
import { DIVIDENDS_USER_VISIBLE } from '../lib/dividendsUserVisible';
import '../styles/CalculatorPage.css';

interface MineProductionPageProps {
  onNavigate: (page: string) => void;
}

const MineProductionPage: React.FC<MineProductionPageProps> = ({ onNavigate }) => {
  const [activeTab, setActiveTab] = useState<string>('technology');

  // Get live gold price
  const { price: liveGoldPrice, isLive: isGoldPriceLive } = useGoldPrice();
  const goldPricePerKg = liveGoldPrice || CALCULATION_CONSTANTS.GOLD_PRICE_USD_PER_KG;

  // Calculate production metrics from constants
  const productionMetrics = useMemo(() => {
    const dailyTonnage = CALCULATION_CONSTANTS.PLANT_CAPACITY_TPH * CALCULATION_CONSTANTS.EFFECTIVE_HOURS_PER_DAY;
    const yearlyTonnage = dailyTonnage * CALCULATION_CONSTANTS.OPERATING_DAYS_PER_YEAR;
    // Grade is grams of recovered gold per tonne of throughput (recovery already baked in)
    const goldGramsPerTon = CALCULATION_CONSTANTS.DEFAULT_IN_SITU_GRADE_G_PER_TONNE;
    const goldKgPerPlantPerYear = (yearlyTonnage * goldGramsPerTon) / 1000;

    return {
      dailyTonnage,
      yearlyTonnage,
      goldKgPerPlantPerYear,
      revenuePerPlantPerYear: goldKgPerPlantPerYear * goldPricePerKg
    };
  }, [goldPricePerKg]);

  // Get expansion data for 2030 (full scale)
  const fullScaleExpansion = EXPANSION_PLAN[2030];
  const projection2030 = DIVIDEND_PROJECTIONS[2030];

  const tabs = [
    { id: 'technology', label: 'Technology', icon: '⚙️' },
    { id: 'comparison', label: 'Why Wash Plants', icon: '🌱' },
    { id: 'specifications', label: 'Specifications', icon: '📊' },
    { id: 'environmental', label: 'Environmental', icon: '🌍' },
    { id: 'operations', label: 'Operations', icon: '🎬' }
  ];

  return (
    <div className="page calculator-page-tabbed">
      {/* Enhanced Page Header */}
      <section className="tabbed-page-header">
        <div className="container">
          <div className="breadcrumb">
            <button onClick={() => onNavigate('home')} className="breadcrumb-link">
              Home
            </button>
            <span className="breadcrumb-separator">→</span>
            <span className="breadcrumb-current">Mine Production</span>
          </div>

          <div className="header-content">
            <div className="header-text">
              <h1 className="page-title">Sustainable Gold Mining Operations</h1>
              <p className="page-subtitle">
                Discover why Aureus Alliance Holdings chose eco-friendly wash plants over traditional strip mining
                for sustainable, profitable, and environmentally responsible gold extraction
              </p>
            </div>
            <div className="header-stats">
              <div className="stat-item">
                <span className="stat-value gold-shimmer">
                  <AnimatedNumber value={fullScaleExpansion?.plants || 200} />
                </span>
                <span className="stat-label">Wash Plants by 2030</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">
                  <AnimatedNumber value={fullScaleExpansion?.hectares || 6000} suffix=" ha" />
                </span>
                <span className="stat-label">Total Land Area</span>
              </div>
              <div className="stat-item">
                <span className="stat-value text-green-400">
                  <AnimatedNumber value={projection2030?.goldProductionKgPerYear || 21000} suffix=" kg" />
                </span>
                <span className="stat-label">Annual Gold Output</span>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="tab-navigation">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className="tab-icon">{tab.icon}</span>
                <span className="tab-label">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tab Content */}
      <section className="tab-content-section">
        <div className="container">
          <div className="tab-content">

            {/* Technology Tab */}
            {activeTab === 'technology' && (
              <div className="fade-in">
                {/* Live Stats Banner */}
                <div style={{
                  background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.15) 0%, rgba(17, 24, 39, 0.95) 100%)',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  borderRadius: '16px',
                  padding: '24px',
                  marginBottom: '32px',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '24px'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#6B7280', fontSize: '0.85rem', marginBottom: '8px' }}>
                      Live Gold Price <InfoTooltip {...({ term: "Gold Price" } as any)} />
                    </div>
                    <div className="gold-shimmer" style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                      <AnimatedCurrency value={goldPricePerKg} /> /kg
                    </div>
                    {isGoldPriceLive && (
                      <span style={{ color: '#10B981', fontSize: '0.75rem' }}>● Live</span>
                    )}
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#6B7280', fontSize: '0.85rem', marginBottom: '8px' }}>
                      Daily Processing
                    </div>
                    <div style={{ color: '#fff', fontSize: '1.5rem', fontWeight: 'bold' }}>
                      <AnimatedNumber value={Math.round(productionMetrics.dailyTonnage)} suffix=" tons" />
                    </div>
                    <span style={{ color: '#9CA3AF', fontSize: '0.75rem' }}>per plant</span>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#6B7280', fontSize: '0.85rem', marginBottom: '8px' }}>
                      Annual Gold Output
                    </div>
                    <div style={{ color: '#10B981', fontSize: '1.5rem', fontWeight: 'bold' }}>
                      <AnimatedNumber value={Math.round(productionMetrics.goldKgPerPlantPerYear)} suffix=" kg" />
                    </div>
                    <span style={{ color: '#9CA3AF', fontSize: '0.75rem' }}>per plant</span>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#6B7280', fontSize: '0.85rem', marginBottom: '8px' }}>
                      Revenue Potential
                    </div>
                    <div className="gold-shimmer" style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                      <AnimatedCurrency value={productionMetrics.revenuePerPlantPerYear} />
                    </div>
                    <span style={{ color: '#9CA3AF', fontSize: '0.75rem' }}>per plant/year</span>
                  </div>
                </div>

                <div className="section-header">
                  <h2>What Are Gold Wash Plants?</h2>
                  <p>Advanced, eco-friendly gold processing technology</p>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: '24px',
                  marginBottom: '32px'
                }}>
                  {[
                    { icon: '⚙️', title: 'Advanced Processing Technology', color: '#D4AF37', desc: 'Gold wash plants are sophisticated, mobile processing units that use water-based separation techniques to extract gold from alluvial deposits. Unlike traditional mining methods, wash plants process material above ground with minimal environmental disruption.' },
                    { icon: '🌊', title: 'Water-Based Separation', color: '#3B82F6', desc: 'Using gravity separation and water flow, wash plants efficiently separate gold particles from sand, gravel, and other materials. This process is highly effective for placer deposits and requires significantly less energy than traditional mining methods.' },
                    { icon: '🚛', title: 'Mobile & Scalable', color: '#10B981', desc: `Our wash plants are designed for mobility and scalability. Each unit can process significant volumes of material while being easily relocated as we expand across our ${(fullScaleExpansion?.hectares || 6000).toLocaleString()}+ hectares of gold-rich land.` }
                  ].map((card, idx) => (
                    <div key={idx} style={{
                      background: 'rgba(31, 41, 55, 0.9)',
                      border: `1px solid ${card.color}40`,
                      borderRadius: '16px',
                      padding: '24px',
                      transition: 'all 0.3s ease'
                    }}>
                      <div style={{ fontSize: '3rem', marginBottom: '16px' }}>{card.icon}</div>
                      <h3 style={{ color: '#fff', fontSize: '1.2rem', marginBottom: '12px' }}>{card.title}</h3>
                      <p style={{ color: '#9CA3AF', fontSize: '0.95rem', lineHeight: '1.6' }}>{card.desc}</p>
                    </div>
                  ))}
                </div>

                {/* Expansion Timeline */}
                <div style={{
                  background: 'rgba(31, 41, 55, 0.9)',
                  border: '1px solid #374151',
                  borderRadius: '16px',
                  padding: '32px'
                }}>
                  <h3 style={{ color: '#D4AF37', fontSize: '1.3rem', marginBottom: '24px', textAlign: 'center' }}>
                    📈 Expansion Timeline (2026-2030)
                  </h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, 1fr)',
                    gap: '16px'
                  }}>
                    {Object.entries(EXPANSION_PLAN).map(([year, data], _idx) => {
                      const isFullScale = year === '2030';
                      return (
                        <div key={year} style={{
                          background: isFullScale ? 'rgba(212, 175, 55, 0.15)' : 'rgba(0,0,0,0.3)',
                          border: `1px solid ${isFullScale ? '#D4AF37' : '#374151'}`,
                          borderRadius: '12px',
                          padding: '16px',
                          textAlign: 'center'
                        }}>
                          <div style={{ color: isFullScale ? '#D4AF37' : '#9CA3AF', fontSize: '0.85rem', marginBottom: '8px' }}>{year}</div>
                          <div style={{ color: '#fff', fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '4px' }}>
                            {data.plants}
                          </div>
                          <div style={{ color: '#6B7280', fontSize: '0.75rem' }}>plants</div>
                          <div style={{
                            marginTop: '12px',
                            paddingTop: '12px',
                            borderTop: '1px solid rgba(255,255,255,0.1)',
                            color: '#10B981',
                            fontSize: '0.85rem',
                            fontWeight: 'bold'
                          }}>
                            {data.hectares.toLocaleString()} ha
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Comparison Tab */}
            {activeTab === 'comparison' && (
              <div className="fade-in">
                <div className="section-header">
                  <h2>Why Wash Plants Over Strip Mining?</h2>
                  <p>Environmental responsibility meets operational efficiency</p>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
                  gap: '32px',
                  marginBottom: '32px'
                }}>
                  {/* Wash Plants */}
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(31, 41, 55, 0.95) 100%)',
                    border: '2px solid #10B981',
                    borderRadius: '20px',
                    padding: '32px',
                    position: 'relative'
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: '-12px',
                      left: '24px',
                      background: '#10B981',
                      color: '#fff',
                      padding: '4px 16px',
                      borderRadius: '20px',
                      fontSize: '0.85rem',
                      fontWeight: 'bold'
                    }}>OUR CHOICE</div>
                    <h3 style={{ color: '#10B981', fontSize: '1.4rem', marginBottom: '20px', marginTop: '8px' }}>
                      🌱 Wash Plants
                    </h3>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {[
                        { text: 'Minimal environmental impact', icon: '✅' },
                        { text: 'Land can be fully rehabilitated', icon: '✅' },
                        { text: 'Lower operational costs', icon: '✅' },
                        { text: 'Faster setup and deployment', icon: '✅' },
                        { text: 'Mobile and flexible operations', icon: '✅' },
                        { text: 'Renewable energy powered', icon: '✅' },
                        { text: 'Community-friendly operations', icon: '✅' },
                        { text: 'Sustainable long-term approach', icon: '✅' }
                      ].map((item, idx) => (
                        <li key={idx} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '10px 0',
                          borderBottom: idx < 7 ? '1px solid rgba(16, 185, 129, 0.2)' : 'none',
                          color: '#E5E7EB'
                        }}>
                          <span style={{ fontSize: '1.1rem' }}>{item.icon}</span>
                          <span>{item.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Strip Mining */}
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(31, 41, 55, 0.95) 100%)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '20px',
                    padding: '32px',
                    opacity: 0.8
                  }}>
                    <h3 style={{ color: '#EF4444', fontSize: '1.4rem', marginBottom: '20px' }}>
                      ⚠️ Traditional Strip Mining
                    </h3>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {[
                        { text: 'Massive environmental destruction', icon: '❌' },
                        { text: 'Permanent landscape alteration', icon: '❌' },
                        { text: 'High rehabilitation costs', icon: '❌' },
                        { text: 'Lengthy setup and permitting', icon: '❌' },
                        { text: 'Fixed location operations', icon: '❌' },
                        { text: 'High energy consumption', icon: '❌' },
                        { text: 'Community displacement', icon: '❌' },
                        { text: 'Unsustainable practices', icon: '❌' }
                      ].map((item, idx) => (
                        <li key={idx} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '10px 0',
                          borderBottom: idx < 7 ? '1px solid rgba(239, 68, 68, 0.2)' : 'none',
                          color: '#9CA3AF'
                        }}>
                          <span style={{ fontSize: '1.1rem' }}>{item.icon}</span>
                          <span>{item.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Cost Comparison */}
                <div style={{
                  background: 'rgba(31, 41, 55, 0.9)',
                  border: '1px solid #374151',
                  borderRadius: '16px',
                  padding: '32px'
                }}>
                  <h3 style={{ color: '#D4AF37', fontSize: '1.3rem', marginBottom: '24px', textAlign: 'center' }}>
                    💰 Cost & Efficiency Comparison
                  </h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '24px'
                  }}>
                    {[
                      { metric: 'Setup Time', washPlant: '2-4 weeks', strip: '6-12 months', better: 'wash' },
                      { metric: 'Setup Cost', washPlant: '$200K-500K', strip: '$5M-20M', better: 'wash' },
                      { metric: 'Operating Cost', washPlant: 'Low', strip: 'Very High', better: 'wash' },
                      { metric: 'Land Recovery', washPlant: '100%', strip: '20-40%', better: 'wash' },
                      { metric: 'Water Usage', washPlant: '90% Recycled', strip: 'High Waste', better: 'wash' },
                      { metric: 'Carbon Footprint', washPlant: 'Minimal', strip: 'High', better: 'wash' }
                    ].map((item, idx) => (
                      <div key={idx} style={{
                        background: 'rgba(0,0,0,0.3)',
                        borderRadius: '12px',
                        padding: '16px',
                        textAlign: 'center'
                      }}>
                        <div style={{ color: '#6B7280', fontSize: '0.85rem', marginBottom: '12px' }}>{item.metric}</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                          <div>
                            <div style={{ color: '#10B981', fontSize: '0.9rem', fontWeight: 'bold' }}>{item.washPlant}</div>
                            <div style={{ color: '#6B7280', fontSize: '0.7rem' }}>Wash Plant</div>
                          </div>
                          <div>
                            <div style={{ color: '#EF4444', fontSize: '0.9rem' }}>{item.strip}</div>
                            <div style={{ color: '#6B7280', fontSize: '0.7rem' }}>Strip Mine</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Specifications Tab */}
            {activeTab === 'specifications' && (
              <div className="fade-in">
                <div className="section-header">
                  <h2>Technical Excellence</h2>
                  <p>State-of-the-art specifications and capabilities derived from operational data</p>
                </div>

                {/* Main Technical Specs */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '24px',
                  marginBottom: '32px'
                }}>
                  {[
                    {
                      icon: '⚙️',
                      title: 'Processing Capacity',
                      value: `${CALCULATION_CONSTANTS.PLANT_CAPACITY_TPH}`,
                      unit: 'tons/hour',
                      color: '#D4AF37',
                      desc: `Each wash plant processes ${CALCULATION_CONSTANTS.PLANT_CAPACITY_TPH} tons of material per hour, ensuring efficient gold extraction`,
                      verified: true
                    },
                    {
                      icon: '📊',
                      title: 'Recovery Rate',
                      value: `${CALCULATION_CONSTANTS.DEFAULT_RECOVERY_FACTOR_PERCENT}%`,
                      unit: '',
                      color: '#10B981',
                      desc: 'Industry-leading gold recovery rates through advanced separation technology',
                      verified: true
                    },
                    {
                      icon: '💧',
                      title: 'Water Efficiency',
                      value: '90%',
                      unit: 'Recycled',
                      color: '#3B82F6',
                      desc: 'Closed-loop water system with 90% water recycling for minimal environmental impact',
                      verified: true
                    },
                    {
                      icon: '⚡',
                      title: 'Power Source',
                      value: '100%',
                      unit: 'Renewable',
                      color: '#F59E0B',
                      desc: 'Solar and wind-powered operations for completely sustainable energy consumption',
                      verified: true
                    }
                  ].map((spec, idx) => (
                    <div key={idx} style={{
                      background: 'rgba(31, 41, 55, 0.9)',
                      border: `1px solid ${spec.color}40`,
                      borderRadius: '16px',
                      padding: '24px',
                      position: 'relative'
                    }}>
                      {spec.verified && (
                        <div style={{
                          position: 'absolute',
                          top: '12px',
                          right: '12px',
                          background: 'rgba(16, 185, 129, 0.2)',
                          color: '#10B981',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '0.7rem',
                          fontWeight: 'bold'
                        }}>✓ Verified</div>
                      )}
                      <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>{spec.icon}</div>
                      <h3 style={{ color: '#9CA3AF', fontSize: '0.9rem', marginBottom: '8px' }}>{spec.title}</h3>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '12px' }}>
                        <span className="gold-shimmer" style={{ fontSize: '2rem', fontWeight: 'bold' }}>{spec.value}</span>
                        <span style={{ color: '#6B7280', fontSize: '1rem' }}>{spec.unit}</span>
                      </div>
                      <p style={{ color: '#6B7280', fontSize: '0.85rem', lineHeight: '1.5' }}>{spec.desc}</p>
                    </div>
                  ))}
                </div>

                {/* Detailed Constants Table */}
                <div style={{
                  background: 'rgba(31, 41, 55, 0.9)',
                  border: '1px solid #374151',
                  borderRadius: '16px',
                  padding: '32px'
                }}>
                  <h3 style={{ color: '#D4AF37', fontSize: '1.3rem', marginBottom: '24px' }}>
                    📋 Operational Parameters
                  </h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '16px'
                  }}>
                    {[
                      { label: 'Recovered Gold Grade', value: `${CALCULATION_CONSTANTS.DEFAULT_IN_SITU_GRADE_G_PER_TONNE} g/tonne`, tooltip: 'Gold Grade' },
                      { label: 'Bulk Density', value: `${CALCULATION_CONSTANTS.BULK_DENSITY_T_PER_M3} ton/m³`, tooltip: 'Bulk Density' },
                      { label: 'Effective Hours/Day', value: `${CALCULATION_CONSTANTS.EFFECTIVE_HOURS_PER_DAY} hours`, tooltip: null },
                      { label: 'Operating Days/Year', value: `${CALCULATION_CONSTANTS.OPERATING_DAYS_PER_YEAR} days`, tooltip: null },
                      { label: 'Daily Processing', value: `${(CALCULATION_CONSTANTS.PLANT_CAPACITY_TPH * CALCULATION_CONSTANTS.EFFECTIVE_HOURS_PER_DAY).toLocaleString()} tons/plant`, tooltip: null },
                      { label: 'Annual Processing', value: `${(CALCULATION_CONSTANTS.PLANT_CAPACITY_TPH * CALCULATION_CONSTANTS.EFFECTIVE_HOURS_PER_DAY * CALCULATION_CONSTANTS.OPERATING_DAYS_PER_YEAR).toLocaleString()} tons/plant`, tooltip: null }
                    ].map((param, idx) => (
                      <div key={idx} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 16px',
                        background: 'rgba(0,0,0,0.3)',
                        borderRadius: '8px'
                      }}>
                        <span style={{ color: '#9CA3AF', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {param.label}
                          {param.tooltip && <InfoTooltip {...({ term: param.tooltip as keyof typeof TERM_EXPLANATIONS } as any)} />}
                        </span>
                        <span style={{ color: '#fff', fontWeight: 'bold' }}>{param.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Environmental Tab */}
            {activeTab === 'environmental' && (
              <div className="fade-in">
                <div className="section-header">
                  <h2>Environmental Stewardship</h2>
                  <p>Protecting Africa's natural heritage while creating value</p>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                  gap: '24px',
                  marginBottom: '32px'
                }}>
                  {[
                    { icon: '🌍', title: 'Land Rehabilitation', color: '#10B981', desc: 'After gold extraction, all land is restored to its natural, viable state. We replant indigenous vegetation and ensure the ecosystem is fully rehabilitated.', stat: '100%', statLabel: 'Land Recovery' },
                    { icon: '💧', title: 'Water Conservation', color: '#3B82F6', desc: 'Our closed-loop water system minimizes water usage and prevents contamination of local water sources, protecting community water supplies.', stat: '90%', statLabel: 'Water Recycled' },
                    { icon: '🌱', title: 'Carbon Neutral', color: '#22C55E', desc: 'Renewable energy powers all operations, making our mining activities carbon neutral and contributing to global climate goals.', stat: '0', statLabel: 'Carbon Emissions' },
                    { icon: '🏘️', title: 'Community Integration', color: '#F59E0B', desc: 'Our operations create local jobs and support community development without displacing residents or disrupting traditional livelihoods.', stat: '500+', statLabel: 'Local Jobs' }
                  ].map((item, idx) => (
                    <div key={idx} style={{
                      background: 'rgba(31, 41, 55, 0.9)',
                      border: `1px solid ${item.color}40`,
                      borderRadius: '16px',
                      padding: '24px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                        <div style={{ fontSize: '2.5rem' }}>{item.icon}</div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ color: item.color, fontSize: '1.5rem', fontWeight: 'bold' }}>{item.stat}</div>
                          <div style={{ color: '#6B7280', fontSize: '0.75rem' }}>{item.statLabel}</div>
                        </div>
                      </div>
                      <h3 style={{ color: '#fff', fontSize: '1.1rem', marginBottom: '12px' }}>{item.title}</h3>
                      <p style={{ color: '#9CA3AF', fontSize: '0.9rem', lineHeight: '1.6' }}>{item.desc}</p>
                    </div>
                  ))}
                </div>

                {/* Why This Matters to Shareholders */}
                <div style={{
                  background: 'rgba(31, 41, 55, 0.9)',
                  border: '1px solid #374151',
                  borderRadius: '16px',
                  padding: '32px'
                }}>
                  <h3 style={{ color: '#D4AF37', fontSize: '1.3rem', marginBottom: '24px', textAlign: 'center' }}>
                    💎 Why This Matters to Shareholders
                  </h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: '20px'
                  }}>
                    {[
                      { icon: '🎯', title: 'Lower Risk Profile', desc: 'Eco-friendly operations face fewer regulatory challenges, community opposition, and environmental liabilities.' },
                      { icon: '📈', title: 'Scalable Growth', desc: `Mobile wash plants allow rapid expansion across our ${(fullScaleExpansion?.hectares || 6000).toLocaleString()}+ hectares.` },
                      { icon: '🌟', title: 'ESG Compliance', desc: 'Environmental, Social, and Governance compliance attracts institutional shareholders.' },
                      { icon: '💰', title: 'Cost Efficiency', desc: 'Lower operational costs and renewable energy usage translate to higher profit margins.' }
                    ].map((item, idx) => (
                      <div key={idx} style={{
                        background: 'rgba(0,0,0,0.3)',
                        borderRadius: '12px',
                        padding: '20px'
                      }}>
                        <div style={{ fontSize: '1.5rem', marginBottom: '12px' }}>{item.icon}</div>
                        <h4 style={{ color: '#fff', fontSize: '1rem', marginBottom: '8px' }}>{item.title}</h4>
                        <p style={{ color: '#9CA3AF', fontSize: '0.85rem', lineHeight: '1.5' }}>{item.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Operations Tab */}
            {activeTab === 'operations' && (
              <div className="fade-in">
                <div className="section-header">
                  <h2>Operations in Action</h2>
                  <p>Visual documentation of our mining operations and equipment</p>
                </div>

                {/* Video Placeholders */}
                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{ color: '#D4AF37', fontSize: '1.2rem', marginBottom: '20px' }}>📹 Operational Videos</h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: '20px'
                  }}>
                    {[
                      { icon: '🎬', title: 'Wash Plant Operations', desc: 'Live footage of our wash plants processing gold-bearing gravel' },
                      { icon: '🚛', title: 'Equipment in Action', desc: 'Heavy machinery and excavation equipment at work' },
                      { icon: '⚡', title: 'Solar Power Systems', desc: 'Renewable energy powering our sustainable operations' }
                    ].map((video, idx) => (
                      <div key={idx} style={{
                        background: 'rgba(31, 41, 55, 0.9)',
                        border: '1px solid #374151',
                        borderRadius: '16px',
                        padding: '24px',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          width: '80px',
                          height: '80px',
                          background: 'rgba(212, 175, 55, 0.1)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '2rem',
                          margin: '0 auto 16px'
                        }}>{video.icon}</div>
                        <h4 style={{ color: '#fff', fontSize: '1rem', marginBottom: '8px' }}>{video.title}</h4>
                        <p style={{ color: '#9CA3AF', fontSize: '0.85rem', marginBottom: '12px' }}>{video.desc}</p>
                        <span style={{
                          background: 'rgba(59, 130, 246, 0.2)',
                          color: '#3B82F6',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '0.75rem'
                        }}>Coming Soon</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Photo Placeholders */}
                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{ color: '#D4AF37', fontSize: '1.2rem', marginBottom: '20px' }}>📸 Operational Photography</h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '16px'
                  }}>
                    {[
                      { icon: '🏭', title: 'Processing Facilities', desc: 'High-resolution images of our wash plant installations' },
                      { icon: '🌍', title: 'Site Locations', desc: 'Aerial and ground-level views of our mining sites' },
                      { icon: '👥', title: 'Team Operations', desc: 'Our professional team managing daily operations' },
                      { icon: '🥇', title: 'Gold Recovery', desc: 'Documentation of gold recovery and processing results' }
                    ].map((photo, idx) => (
                      <div key={idx} style={{
                        background: 'rgba(31, 41, 55, 0.9)',
                        border: '1px solid #374151',
                        borderRadius: '12px',
                        padding: '20px',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '2rem', marginBottom: '12px' }}>{photo.icon}</div>
                        <h4 style={{ color: '#fff', fontSize: '0.95rem', marginBottom: '6px' }}>{photo.title}</h4>
                        <p style={{ color: '#6B7280', fontSize: '0.8rem' }}>{photo.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Media Note */}
                <div style={{
                  background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(31, 41, 55, 0.95) 100%)',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: '16px',
                  padding: '24px',
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'flex-start'
                }}>
                  <div style={{ fontSize: '2rem' }}>ℹ️</div>
                  <div>
                    <h4 style={{ color: '#3B82F6', fontSize: '1.1rem', marginBottom: '8px' }}>Media Updates</h4>
                    <p style={{ color: '#9CA3AF', fontSize: '0.9rem', lineHeight: '1.6' }}>
                      We continuously document our operations through photos and videos during site visits.
                      This media library will be regularly updated to provide transparency and proof of our active mining operations.
                    </p>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.15) 0%, rgba(17, 24, 39, 0.95) 100%)',
        padding: '48px 0',
        textAlign: 'center'
      }}>
        <div className="container">
          <h2 style={{ color: '#D4AF37', fontSize: '1.8rem', marginBottom: '16px' }}>
            Ready to Purchase Shares in Sustainable Gold Mining?
          </h2>
          <p style={{ color: '#9CA3AF', fontSize: '1.1rem', marginBottom: '24px', maxWidth: '600px', margin: '0 auto 24px' }}>
            Join Aureus Alliance Holdings and be part of Africa's most innovative
            and environmentally responsible gold mining operation.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              className="btn btn-primary"
              onClick={() => onNavigate('investment-phases')}
              style={{
                background: 'linear-gradient(135deg, #D4AF37, #B8860B)',
                border: 'none',
                padding: '14px 32px',
                borderRadius: '12px',
                color: '#000',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              View Share Phases
            </button>
            {DIVIDENDS_USER_VISIBLE && (
            <button
              className="btn btn-secondary"
              onClick={() => onNavigate('calculator')}
              style={{
                background: 'transparent',
                border: '2px solid #D4AF37',
                padding: '14px 32px',
                borderRadius: '12px',
                color: '#D4AF37',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Calculate Dividends
            </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default MineProductionPage;
